import type { ColorMode } from '@/types/index'; 

interface ColorModeCardProps {
  value: ColorMode;
  supportsColor: boolean;
  bwRate: number;
  colorRate: number;
  onChange: (value: ColorMode) => void;
}

export function ColorModeCard({
  value,
  supportsColor,
  bwRate,
  colorRate,
  onChange,
}: ColorModeCardProps) {
  return (
    <section className="bg-surface-lowest rounded-4 shadow-kiosk-sm p-3 p-lg-4">
      <div className="d-flex align-items-center justify-content-between mb-1">
        <h3 className="text-label-md text-on-surface fw-bold mb-0 d-none d-lg-block">
          Color Mode
        </h3>
        <span className="text-label-md text-on-surface d-lg-none">
          Color Palette
        </span>
        <span className="badge bg-surface-container text-on-surface-variant fw-medium d-none d-lg-inline">
          Laser Engine
        </span>
        <span className="text-body-sm text-on-surface-variant d-lg-none">
          Standard Rate Applies
        </span>
      </div>

      <p className="text-body-sm text-on-surface-variant mb-3 d-none d-lg-block">
        Per-impression calculation dynamically updates
      </p>

      <div className="row row-cols-2 g-3">
        <div className="col">
          <button
            type="button"
            className={`btn w-100 h-100 text-start p-3 rounded-4 border-0 position-relative ${
              value === 'bw'
                ? 'bg-surface-high shadow-sm'
                : 'bg-surface-lowest shadow-kiosk-sm'
            }`}
            onClick={() => onChange('bw')}
          >
            {value === 'bw' && (
              <span
                className="position-absolute top-0 end-0 mt-2 me-2 rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                style={{ width: 22, height: 22 }}
              >
                <i className="bi bi-check-lg" style={{ fontSize: 14 }} />
              </span>
            )}

            <div
              className="rounded-3 bg-surface-highest d-flex align-items-center justify-content-center text-on-surface mb-3"
              style={{ width: 40, height: 40 }}
            >
              <i className="bi bi-circle-half" style={{ fontSize: 20 }} />
            </div>

            <span className="text-label-lg text-on-surface fw-bold d-block">
              Monochrome
            </span>

            <span className="text-body-sm text-on-surface-variant d-block mb-1">
              Sharp Crisp Greyscale
            </span>

            <span className="text-headline-sm text-primary fw-bold">
              ₹{bwRate.toFixed(2)}{' '}
              <span className="text-body-sm text-on-surface-variant fw-normal">
                / page
              </span>
            </span>
          </button>
        </div>

        {supportsColor && (
          <div className="col">
            <button
              type="button"
              className={`btn w-100 h-100 text-start p-3 rounded-4 border-0 position-relative ${
                value === 'color'
                  ? 'bg-surface-high shadow-sm'
                  : 'bg-surface-lowest shadow-kiosk-sm'
              }`}
              onClick={() => onChange('color')}
            >
              {value === 'color' && (
                <span
                  className="position-absolute top-0 end-0 mt-2 me-2 rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                  style={{ width: 22, height: 22 }}
                >
                  <i className="bi bi-check-lg" style={{ fontSize: 14 }} />
                </span>
              )}

              <div
                className="rounded-3 bg-surface-container d-flex align-items-center justify-content-center text-primary mb-3"
                style={{ width: 40, height: 40 }}
              >
                <i className="bi bi-palette" style={{ fontSize: 20 }} />
              </div>

              <span className="text-label-lg text-on-surface fw-bold d-block">
                Full Color
              </span>

              <span className="text-body-sm text-on-surface-variant d-block mb-1">
                Rich Vibrant Pigment
              </span>

              <span
                className={`text-headline-sm fw-bold ${
                  value === 'color' ? 'text-primary' : 'text-on-surface'
                }`}
              >
                ₹{colorRate.toFixed(2)}{' '}
                <span className="text-body-sm text-on-surface-variant fw-normal">
                  / page
                </span>
              </span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}